import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { Alert } from './Alert';
import { Field } from './Field';
import { MfaPairing } from './MfaPairing';
import { TITRE_VERROU_BETA } from './CompteBetaAvis';

/**
 * Double authentification, depuis « Mon compte » : état, activation, retrait.
 *
 * Deux précautions au-delà de l'appairage lui-même (cf. MfaPairing) :
 *
 *  1. On ne propose PAS de ré-appairer un TOTP déjà actif. `mfa.setup()` remet
 *     `confirmedAt` à null : une tentative abandonnée en cours de route
 *     laisserait le compte sans second facteur utilisable, sans que personne ne
 *     s'en aperçoive avant la prochaine connexion. Pour changer d'appareil :
 *     désactiver, puis réactiver — deux gestes explicites.
 *  2. Le retrait exige le mot de passe courant, vérifié par le serveur. Une
 *     session ouverte ne suffit pas à baisser sa propre garde.
 */

interface Props {
  enabled: boolean;
  /** Rafraîchit le compte après un changement — l'état vient du serveur. */
  onChanged: () => void;
  /** Compte bêta partagé (03/10/2026) : ni activation ni retrait. */
  verrouille?: boolean;
}

type Mode = 'idle' | 'pairing' | 'disabling';

export function MfaSection({ enabled, onChanged, verrouille = false }: Props) {
  const [mode, setMode] = useState<Mode>('idle');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function confirmDisable(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const currentPassword = new FormData(event.currentTarget).get('currentPassword');
    setError(null);
    setPending(true);
    try {
      await api.post('/auth/mfa/disable', { currentPassword: String(currentPassword ?? '') });
      setMode('idle');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Mot de passe refusé.');
    } finally {
      setPending(false);
    }
  }

  return (
    <section id="double-authentification" style={{ margin: '0 0 1.5rem', scrollMarginTop: '1rem' }}>
      <h2 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>Double authentification</h2>
      <Alert message={error} />

      {mode === 'pairing' ? (
        <MfaPairing
          onDone={() => {
            setMode('idle');
            onChanged();
          }}
          onCancel={() => setMode('idle')}
        />
      ) : mode === 'disabling' ? (
        <form onSubmit={(event) => void confirmDisable(event)}>
          <p style={{ fontSize: '0.9rem', margin: '0 0 0.5rem' }}>
            Confirmez avec votre mot de passe. Vos codes de secours seront effacés.
          </p>
          <Field
            label="Mot de passe actuel"
            name="currentPassword"
            type="password"
            autoComplete="current-password"
            required
          />
          <button className="btn" type="submit" disabled={pending}>
            {pending ? 'Vérification…' : 'Désactiver'}
          </button>
          <button className="btn btn-secondary" type="button" onClick={() => setMode('idle')}>
            Annuler
          </button>
        </form>
      ) : enabled ? (
        <>
          <p style={{ fontSize: '0.9rem', margin: '0 0 0.5rem' }}>
            Activée. Pour changer d’appareil, désactivez puis réactivez — deux gestes explicites
            valent mieux qu’un ré-appairage qui pourrait rester à moitié fait.
          </p>
          <button
            className="btn btn-secondary"
            type="button"
            disabled={verrouille}
            title={verrouille ? TITRE_VERROU_BETA : undefined}
            onClick={() => setMode('disabling')}
          >
            Désactiver
          </button>
        </>
      ) : (
        <>
          <p style={{ fontSize: '0.9rem', margin: '0 0 0.5rem' }}>
            Désactivée. Elle est obligatoire pour accéder à l’administration de la suite.
          </p>
          <button
            className="btn"
            type="button"
            disabled={verrouille}
            title={verrouille ? TITRE_VERROU_BETA : undefined}
            onClick={() => setMode('pairing')}
          >
            Activer
          </button>
        </>
      )}
    </section>
  );
}
